import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Divider,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  Palette as PaletteIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Tile, TileFormData } from '../../types';
import { useDashboard } from '../../hooks/useDashboard';
import ColorPicker from '../Theme/ColorPicker';
import IconPicker from '../IconPicker/IconPicker';
import IconDisplay from '../IconDisplay/IconDisplay';
import { getTileIcon, getFaviconUrl } from '../../utils/logoDetection';

interface TileEditDialogProps {
  open: boolean;
  onClose: () => void;
  tile?: Tile;
  groupId?: string;
  mode: 'create' | 'edit';
  onSubmit?: (data: TileFormData) => Promise<void>; // Optional custom submit handler for preview mode
}

const schema: yup.ObjectSchema<TileFormData> = yup.object({
  title: yup.string().required('Title is required').min(1, 'Title must be at least 1 character'),
  description: yup.string().optional(),
  url: yup.string().required('URL is required').url('Must be a valid URL'),
  icon: yup.string().optional(), // Icon will be auto-detected from URL
  iconType: yup.string().oneOf(['material', 'service', 'custom']).optional(),
  iconUrl: yup.string().optional(),
  color: yup.string().required('Color is required').matches(/^#[0-9A-F]{6}$/i, 'Please enter a valid hex color'),
  openInNewTab: yup.boolean().optional(),
  isVisible: yup.boolean().optional(),
  groupId: yup.string().optional(),
});

// Safe hook wrapper that returns null if context is unavailable
const useSafeDashboard = () => {
  try {
    return useDashboard();
  } catch (e) {
    return null;
  }
};

const TileEditDialog: React.FC<TileEditDialogProps> = ({
  open,
  onClose,
  tile,
  groupId,
  mode,
  onSubmit: customOnSubmit
}) => {
  // Always call hooks unconditionally
  const dashboardContext = useSafeDashboard();

  // Use dashboard methods only if not using custom handler and context is available
  const createTile = customOnSubmit ? undefined : dashboardContext?.createTile;
  const updateTile = customOnSubmit ? undefined : dashboardContext?.updateTile;
  const config = customOnSubmit ? null : dashboardContext?.config;
  const activeTab = customOnSubmit ? null : dashboardContext?.activeTab;
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoDetectedIcon, setAutoDetectedIcon] = useState<{type: string, value: string, fallback?: string} | null>(null);
  const [isManualIcon, setIsManualIcon] = useState(false); // Track if user manually selected an icon

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid }
  } = useForm<TileFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: tile?.title || '',
      description: tile?.description || '',
      url: tile?.url || '',
      icon: tile?.icon || '',
      iconType: tile?.iconType || 'service',
      iconUrl: tile?.iconUrl || '',
      color: tile?.color || '#1976d2',
      openInNewTab: tile?.openInNewTab ?? true,
      isVisible: tile?.isVisible ?? true,
      groupId: groupId || activeTab?.groups[0]?.id || '',
    },
    mode: 'onChange'
  });

  const watchedColor = watch('color');
  const watchedIcon = watch('icon');
  const watchedIconType = watch('iconType');
  const watchedIconUrl = watch('iconUrl');
  const watchedTitle = watch('title');
  const watchedUrl = watch('url');

  // Reset form when dialog opens/closes or tile changes
  useEffect(() => {
    if (open) {
      const isExistingTile = !!tile;
      // For new tiles, start with auto-detection. For existing tiles, assume manually set
      setIsManualIcon(isExistingTile && (tile.iconType !== 'service' || !tile.iconUrl?.includes('google.com/s2/favicons')));
      
      reset({
        title: tile?.title || '',
        description: tile?.description || '',
        url: tile?.url || '',
        icon: tile?.icon || '',
        iconType: tile?.iconType || 'service',
        iconUrl: tile?.iconUrl || '',
        color: tile?.color || '#1976d2',
        openInNewTab: tile?.openInNewTab ?? true,
        isVisible: tile?.isVisible ?? true,
        groupId: groupId || activeTab?.groups[0]?.id || '',
      });
    }
  }, [open, tile, groupId, config, activeTab, reset]);

  // Auto-detect icon when URL changes (only if user hasn't manually selected an icon)
  useEffect(() => {
    if (mode === 'create' && watchedUrl && watchedUrl.startsWith('http') && !isManualIcon && open) {
      const detectedIcon = getTileIcon(watchedUrl, watchedTitle);
      setAutoDetectedIcon(detectedIcon);
      
      // Automatically update the form with detected icon
      setValue('icon', detectedIcon.value);
      setValue('iconType', detectedIcon.type);
      if (detectedIcon.type === 'service' || detectedIcon.type === 'custom') {
        setValue('iconUrl', detectedIcon.value);
      } else {
        setValue('iconUrl', '');
      }
    } else if (!watchedUrl && !isManualIcon && open) {
      // Clear icon when URL is cleared
      setValue('icon', '');
      setValue('iconType', 'service');
      setValue('iconUrl', '');
      setAutoDetectedIcon(null);
    }
  }, [watchedUrl, watchedTitle, isManualIcon, open, setValue]);

  const handleClose = () => {
    reset();
    setShowIconPicker(false);
    setIsSubmitting(false);
    setAutoDetectedIcon(null);
    setIsManualIcon(false);
    onClose();
  };

  const onSubmit: SubmitHandler<TileFormData> = async (data) => {
    setIsSubmitting(true);
    
    try {
      // Ensure we have an icon - if not, auto-detect one
      if (!data.icon && data.url) {
        const detectedIcon = getTileIcon(data.url, data.title);
        data.icon = detectedIcon.value;
        data.iconType = detectedIcon.type;
        if (detectedIcon.type === 'service' || detectedIcon.type === 'custom') {
          data.iconUrl = detectedIcon.value;
        }
      }
      
      // Fallback to material icon if still no icon
      if (!data.icon) {
        data.icon = 'link';
        data.iconType = 'material';
        data.iconUrl = '';
      }
      
      if (customOnSubmit) {
        // Use custom handler for preview mode
        await customOnSubmit(data);
      } else {
        // Use dashboard methods for normal mode
        if (mode === 'create') {
          // First try data.groupId, then passed groupId, then first group in active tab, then legacy structure
          const targetGroupId = data.groupId || groupId || activeTab?.groups[0]?.id || config?.groups[0]?.id;
          
          if (!targetGroupId) {
            throw new Error('No group available to add tile to. Please create a group first.');
          }
          
          console.log('Creating tile with data:', data, 'in group:', targetGroupId);
          
          if (!createTile) {
            throw new Error('Dashboard context not available');
          }
          
          await createTile(targetGroupId, {
            title: data.title,
            description: data.description || '',
            url: data.url,
            icon: data.icon,
            iconType: data.iconType,
            iconUrl: data.iconUrl,
            color: data.color,
            openInNewTab: data.openInNewTab,
            isVisible: data.isVisible,
          });
          
          console.log('Tile created successfully');
        } else if (mode === 'edit' && tile) {
          console.log('Updating tile with data:', data);
          
          if (!updateTile) {
            throw new Error('Dashboard context not available');
          }
          
          await updateTile(tile.id, {
            title: data.title,
            description: data.description || '',
            url: data.url,
            icon: data.icon,
            iconType: data.iconType,
            iconUrl: data.iconUrl,
            color: data.color,
            openInNewTab: data.openInNewTab,
            isVisible: data.isVisible,
          });
          
          console.log('Tile updated successfully');
        }
      }
      
      // Close dialog after successful save
      handleClose();
    } catch (error) {
      console.error('Error saving tile:', error);
      // Show error to user
      alert(`Error saving tile: ${error instanceof Error ? error.message : 'Unknown error'}`);
      // Don't close dialog on error, let user try again
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleColorChange = (color: string) => {
    setValue('color', color);
  };

  const handleIconChange = (icon: string, iconType?: 'material' | 'service' | 'custom', iconUrl?: string) => {
    setValue('icon', icon);
    if (iconType) setValue('iconType', iconType);
    if (iconUrl) setValue('iconUrl', iconUrl);
    setIsManualIcon(true); // Mark that user manually selected an icon
    setShowIconPicker(false);
  };

  const getPreviewUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LinkIcon color="primary" />
            <Typography variant="h6">
              {mode === 'create' ? 'Create New Tile' : 'Edit Tile'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={8}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Tile Title"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="groupId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.groupId}>
                    <InputLabel>Group</InputLabel>
                    <Select {...field} label="Group">
                      {(activeTab?.groups || config?.groups || []).map((group: any) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description (Optional)"
                    multiline
                    rows={2}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="url"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="URL"
                    placeholder="https://example.com"
                    error={!!errors.url}
                    helperText={errors.url?.message || (field.value ? `Preview: ${getPreviewUrl(field.value)}` : '')}
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* Appearance */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Appearance
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <ColorPicker
                label="Tile Color"
                value={watchedColor}
                onChange={handleColorChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Tile Icon
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {/* Icon preview */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconDisplay
                      icon={watchedIcon || 'link'}
                      iconType={watchedIconType as 'material' | 'service' | 'custom'}
                      iconUrl={watchedIconUrl}
                      url={watchedUrl}
                      title={watchedTitle}
                      size={32}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {autoDetectedIcon && !isManualIcon ? '(Auto-detected)' : '(Manual)'}
                    </Typography>
                  </Box>
                  
                  {/* Manual selection button */}
                  <Button
                    variant="outlined"
                    onClick={() => setShowIconPicker(true)}
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    {isManualIcon ? 'Change Icon' : 'Override Icon'}
                  </Button>
                  
                  {/* Reset to auto-detect button */}
                  {isManualIcon && watchedUrl && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setIsManualIcon(false);
                        // Trigger re-detection
                        const detectedIcon = getTileIcon(watchedUrl, watchedTitle);
                        setValue('icon', detectedIcon.value);
                        setValue('iconType', detectedIcon.type);
                        if (detectedIcon.type === 'service' || detectedIcon.type === 'custom') {
                          setValue('iconUrl', detectedIcon.value);
                        } else {
                          setValue('iconUrl', '');
                        }
                      }}
                      sx={{ color: 'primary.main', textDecoration: 'underline' }}
                    >
                      Use Auto-Detected
                    </Button>
                  )}
                </Box>
                
                {/* Editable favicon URL field when using service icon */}
                {(watchedIconType === 'service' || watchedIconType === 'custom') && watchedIconUrl && (
                  <Box sx={{ mt: 2 }}>
                    <Controller
                      name="iconUrl"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Icon URL"
                          placeholder="https://www.google.com/s2/favicons?sz=32&domain=example.com"
                          helperText="You can edit the favicon URL or use a custom image URL"
                          inputRef={(input) => {
                            if (input && field.value && mode === 'edit') {
                              // Set focus and move cursor to end only in edit mode
                              input.focus();
                              input.setSelectionRange(field.value.length, field.value.length);
                            }
                          }}
                          onChange={(e) => {
                            field.onChange(e);
                            setIsManualIcon(true); // Mark as manually edited
                            setValue('icon', e.target.value); // Update icon value to match
                          }}
                        />
                      )}
                    />
                  </Box>
                )}
              </Box>
            </Grid>

            <Divider sx={{ width: '100%', my: 2 }} />

            {/* Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Settings
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="openInNewTab"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Open in new tab"
                    sx={{ display: 'block', mb: 1 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="isVisible"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Visible on dashboard"
                    sx={{ display: 'block', mb: 1 }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={!isValid || isSubmitting}
          sx={{ minWidth: 120 }}
        >
          {isSubmitting ? 'Saving...' : (mode === 'create' ? 'Create Tile' : 'Update Tile')}
        </Button>
      </DialogActions>


      {/* Icon Picker Dialog */}
      <IconPicker
        open={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onIconSelect={handleIconChange}
        selectedIcon={watchedIcon}
        selectedIconType={watchedIconType}
        selectedIconUrl={watchedIconUrl}
        tileUrl={watchedTitle}
        tileTitle={watchedTitle}
      />
    </Dialog>
  );
};

export default TileEditDialog;